import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/Breadcrumb';
import CouponService from '../../services/CouponService';
import CouponInterface from './CouponInterface';
import ConfirmationModal from '../../components/ConfirmtionModel';
import AuthenticationService from '../../services/AuthenticationService';

interface UsageStats {
  totalUsage: number;
  remainingUsage: number;
  usagePercentage: number;
  recentUsages: Array<{
    usedAt: string;
    orderId: string;
    customerEmail: string;
    discountAmount: number;
  }>;
}

const CouponDetails: React.FC = () => {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<CouponInterface | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Check if user has admin permissions
    const currentUser = AuthenticationService.getCurrentUser();
    if (!currentUser || (!currentUser.roles?.includes('ROLE_SUPERADMIN') && !currentUser.roles?.includes('ROLE_ADMIN'))) {
      toast.error('You do not have permission to access coupon management.');
      navigate('/coupons');
      return;
    }

    if (couponId) {
      fetchCouponDetails();
      fetchUsageStats();
    }
  }, [couponId, navigate]);

  const fetchCouponDetails = async () => {
    try {
      const response = await CouponService.getCoupon(parseInt(couponId!));
      setCoupon(response.data);
    } catch (error) {
      console.error('Error fetching coupon details:', error);
      toast.error('Failed to fetch coupon details');
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await CouponService.getCouponUsageStats(parseInt(couponId!));
      setUsageStats(response.data);
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      // Don't show error toast for usage stats as it might not be critical
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCouponStatus = async () => {
    if (!coupon) return;
    
    try {
      await CouponService.toggleCouponStatus(coupon.id);
      setCoupon(prev => prev ? { ...prev, isActive: !prev.isActive } : null);
      toast.success('Coupon status updated successfully');
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      toast.error('Failed to update coupon status');
    }
  };

  const deleteCoupon = async () => {
    if (!coupon) return;
    
    setIsDeleting(true);
    try {
      await CouponService.removeCoupon(coupon.id);
      toast.success('Coupon deleted successfully');
      navigate('/coupons');
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const removeCategoryFromCoupon = async (categoryId: number) => {
    if (!coupon) return;
    
    try {
      // Create updated coupon data without the removed category
      const updatedCategoryIds = coupon.applicableCategories
        ?.filter((cat: any) => cat.categoryId !== categoryId)
        .map((cat: any) => cat.categoryId) || [];
      
      const updatedCouponData = {
        ...coupon,
        applicableCategoryIds: updatedCategoryIds
      };
      
      await CouponService.updateCoupon(coupon.id, updatedCouponData);
      
      // Update local state
      setCoupon(prev => prev ? {
        ...prev,
        applicableCategories: prev.applicableCategories?.filter((cat: any) => cat.categoryId !== categoryId)
      } : null);
      
      toast.success('Category removed successfully');
    } catch (error) {
      console.error('Error removing category:', error);
      toast.error('Failed to remove category');
    }
  };

  const clearAllCategories = async () => {
    if (!coupon) return;
    
    try {
      const updatedCouponData = {
        ...coupon,
        applicableCategoryIds: []
      };
      
      await CouponService.updateCoupon(coupon.id, updatedCouponData);
      
      // Update local state
      setCoupon(prev => prev ? {
        ...prev,
        applicableCategories: []
      } : null);
      
      toast.success('All categories cleared successfully');
    } catch (error) {
      console.error('Error clearing categories:', error);
      toast.error('Failed to clear categories');
    }
  };

  const removeProductFromCoupon = async (productId: number) => {
    if (!coupon) return;
    
    try {
      // Create updated coupon data without the removed product
      const updatedProductIds = coupon.applicableProducts
        ?.filter((prod: any) => prod.productId !== productId)
        .map((prod: any) => prod.productId) || [];
      
      const updatedCouponData = {
        ...coupon,
        applicableProductIds: updatedProductIds
      };
      
      await CouponService.updateCoupon(coupon.id, updatedCouponData);
      
      // Update local state
      setCoupon(prev => prev ? {
        ...prev,
        applicableProducts: prev.applicableProducts?.filter((prod: any) => prod.productId !== productId)
      } : null);
      
      toast.success('Product removed successfully');
    } catch (error) {
      console.error('Error removing product:', error);
      toast.error('Failed to remove product');
    }
  };

  const clearAllProducts = async () => {
    if (!coupon) return;
    
    try {
      const updatedCouponData = {
        ...coupon,
        applicableProductIds: []
      };
      
      await CouponService.updateCoupon(coupon.id, updatedCouponData);
      
      // Update local state
      setCoupon(prev => prev ? {
        ...prev,
        applicableProducts: []
      } : null);
      
      toast.success('All products cleared successfully');
    } catch (error) {
      console.error('Error clearing products:', error);
      toast.error('Failed to clear products');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const isExpired = (validUntil: string) => {
    return new Date(validUntil) < new Date();
  };

  if (isLoading) {
    return (
      <>
        <Breadcrumb pageName="Coupon Details" />
        <div className="flex mt-60 items-center justify-center">
          <svg
            className="h-10 w-10 animate-spin text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.001 8.001 0 0117.709 7H15v5.373L6.373 17H6v-4.709zM20 12h-4v-.373l-5.373-5.372L15 6.627V2h5.373A8.001 8.001 0 0120 12z"
            ></path>
          </svg>
        </div>
      </>
    );
  }

  if (!coupon) {
    return (
      <>
        <Breadcrumb pageName="Coupon Details" />
        <div className="text-center mt-20">
          <h2 className="text-2xl font-bold text-black dark:text-white mb-4">
            Coupon Not Found
          </h2>
          <Link
            to="/coupons"
            className="inline-flex items-center justify-center rounded-md bg-primary py-2 px-4 text-center font-medium text-white hover:bg-opacity-90"
          >
            Back to Coupons
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumb pageName="Coupon Details" />
      
      <div className="grid grid-cols-1 gap-9 sm:grid-cols-2">
        {/* Basic Information */}
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
            <h3 className="font-medium text-black dark:text-white">
              Basic Information
            </h3>
          </div>
          <div className="p-6.5">
            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Coupon Code
              </label>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-primary bg-primary/10 px-3 py-1 rounded">
                  {coupon.couponCode}
                </span>
                <button
                  onClick={() => navigator.clipboard.writeText(coupon.couponCode)}
                  className="text-primary hover:text-primary/80"
                  title="Copy code"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Description
              </label>
              <p className="text-black dark:text-white">
                {coupon.description || 'No description provided'}
              </p>
            </div>

            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Stackable
              </label>
              <p className="text-black dark:text-white">
                {coupon.isStackable ? 'Yes' : 'No'}
              </p>
            </div>

            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Status
              </label>
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex rounded-full py-1 px-3 text-sm font-medium ${
                    coupon.isActive
                      ? 'bg-success bg-opacity-10 text-success'
                      : 'bg-danger bg-opacity-10 text-danger'
                  }`}
                >
                  {coupon.isActive ? 'Active' : 'Inactive'}
                </span>
                {isExpired(coupon.validUntil) && (
                  <span className="inline-flex rounded-full py-1 px-3 text-sm font-medium bg-warning bg-opacity-10 text-warning">
                    Expired
                  </span>
                )}
              </div>
            </div>

            {coupon.createdAt && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Created
                </label>
                <p className="text-black dark:text-white">
                  {formatDate(coupon.createdAt)}
                </p>
              </div>
            )}

            {coupon.updatedAt && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Last Updated
                </label>
                <p className="text-black dark:text-white">
                  {formatDate(coupon.updatedAt)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Discount Information */}
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
            <h3 className="font-medium text-black dark:text-white">
              Discount Information
            </h3>
          </div>
          <div className="p-6.5">
            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Discount Type
              </label>
              <p className="text-black dark:text-white">
                {coupon.discountType === 'PERCENTAGE' ? 'Percentage' : 'Fixed Amount'}
              </p>
            </div>

            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Discount Value
              </label>
              <p className="text-lg font-semibold text-primary">
                {coupon.discountType === 'PERCENTAGE' 
                  ? `${coupon.discountValue}%` 
                  : formatCurrency(coupon.discountValue)}
              </p>
            </div>

            {coupon.minimumOrderValue && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Minimum Order Value
                </label>
                <p className="text-black dark:text-white">
                  {formatCurrency(coupon.minimumOrderValue)}
                </p>
              </div>
            )}

            {coupon.maximumDiscountLimit && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Maximum Discount Limit
                </label>
                <p className="text-black dark:text-white">
                  {formatCurrency(coupon.maximumDiscountLimit)}
                </p>
              </div>
            )}

            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Valid From
              </label>
              <p className="text-black dark:text-white">
                {formatDate(coupon.validFrom)}
              </p>
            </div>

            <div className="mb-4.5">
              <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                Valid Until
              </label>
              <p className={`${isExpired(coupon.validUntil) ? 'text-red-500' : 'text-black dark:text-white'}`}>
                {formatDate(coupon.validUntil)}
              </p>
            </div>

            {coupon.applicableProducts && coupon.applicableProducts.length > 0 && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Applicable Products
                </label>
                <div className="text-black dark:text-white">
                  {coupon.applicableProducts.map((product: any, index: number) => (
                    <span key={product.productId} className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded text-sm mr-2 mb-1">
                      {product.productName}
                      <button
                        onClick={() => removeProductFromCoupon(product.productId)}
                        className="ml-1 text-red-500 hover:text-red-700 font-bold text-xs"
                        title="Remove product"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {coupon.applicableProducts.length > 1 && (
                  <button
                    onClick={clearAllProducts}
                    className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
                  >
                    Clear All Products
                  </button>
                )}
              </div>
            )}

            {coupon.applicableCategories && coupon.applicableCategories.length > 0 && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Applicable Categories
                </label>
                <div className="text-black dark:text-white">
                  {coupon.applicableCategories.map((category: any, index: number) => (
                    <span key={category.categoryId} className="inline-flex items-center gap-1 bg-success/10 text-success px-2 py-1 rounded text-sm mr-2 mb-1">
                      {category.categoryName}
                      <button
                        onClick={() => removeCategoryFromCoupon(category.categoryId)}
                        className="ml-1 text-red-500 hover:text-red-700 font-bold text-xs"
                        title="Remove category"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {coupon.applicableCategories.length > 1 && (
                  <button
                    onClick={clearAllCategories}
                    className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
                  >
                    Clear All Categories
                  </button>
                )}
              </div>
            )}

            {(!coupon.applicableProducts || coupon.applicableProducts.length === 0) && 
             (!coupon.applicableCategories || coupon.applicableCategories.length === 0) && (
              <div className="mb-4.5">
                <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
                  Applicable To
                </label>
                <p className="text-black dark:text-white">
                  All Products (No restrictions)
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage Statistics */}
      <div className="mt-9">
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
            <h3 className="font-medium text-black dark:text-white">
              Usage Statistics
            </h3>
          </div>
          <div className="p-6.5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 mb-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">
                  {coupon.currentUsageCount}
                </p>
                <p className="text-sm text-black dark:text-white">
                  Times Used
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-black dark:text-white">
                  {coupon.totalMaxUsage || '∞'}
                </p>
                <p className="text-sm text-black dark:text-white">
                  Total Max Usage
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-black dark:text-white">
                  {coupon.maxUsagePerUser || '∞'}
                </p>
                <p className="text-sm text-black dark:text-white">
                  Max Per User
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-success">
                  {coupon.totalMaxUsage 
                    ? Math.max(0, coupon.totalMaxUsage - coupon.currentUsageCount)
                    : '∞'
                  }
                </p>
                <p className="text-sm text-black dark:text-white">
                  Remaining Uses
                </p>
              </div>
            </div>

            {coupon.totalMaxUsage && (
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-black dark:text-white">Usage Progress</span>
                  <span className="text-sm text-black dark:text-white">
                    {Math.round((coupon.currentUsageCount / coupon.totalMaxUsage) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                  <div 
                    className="bg-primary h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((coupon.currentUsageCount / coupon.totalMaxUsage) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {usageStats?.recentUsages && usageStats.recentUsages.length > 0 && (
              <div>
                <h4 className="font-medium text-black dark:text-white mb-4">
                  Recent Usage
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="bg-gray-2 dark:bg-meta-4">
                        <th className="py-2 px-4 text-left font-medium text-black dark:text-white">
                          Date
                        </th>
                        <th className="py-2 px-4 text-left font-medium text-black dark:text-white">
                          Order ID
                        </th>
                        <th className="py-2 px-4 text-left font-medium text-black dark:text-white">
                          Customer
                        </th>
                        <th className="py-2 px-4 text-left font-medium text-black dark:text-white">
                          Discount Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageStats.recentUsages.map((usage, index) => (
                        <tr key={index}>
                          <td className="border-b border-[#eee] py-2 px-4 dark:border-strokedark">
                            {formatDate(usage.usedAt)}
                          </td>
                          <td className="border-b border-[#eee] py-2 px-4 dark:border-strokedark">
                            {usage.orderId}
                          </td>
                          <td className="border-b border-[#eee] py-2 px-4 dark:border-strokedark">
                            {usage.customerEmail}
                          </td>
                          <td className="border-b border-[#eee] py-2 px-4 dark:border-strokedark">
                            {formatCurrency(usage.discountAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-9 flex gap-4">
        <Link
          to={`/coupon/update/${coupon.id}`}
          className="inline-flex items-center justify-center rounded-md bg-primary py-2 px-4 text-center font-medium text-white hover:bg-opacity-90"
        >
          Edit Coupon
        </Link>
        <button
          onClick={toggleCouponStatus}
          className={`inline-flex items-center justify-center rounded-md py-2 px-4 text-center font-medium ${
            coupon.isActive
              ? 'bg-warning text-white hover:bg-opacity-90'
              : 'bg-success text-white hover:bg-opacity-90'
          }`}
        >
          {coupon.isActive ? 'Deactivate' : 'Activate'}
        </button>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="inline-flex items-center justify-center rounded-md bg-danger py-2 px-4 text-center font-medium text-white hover:bg-opacity-90"
        >
          Delete Coupon
        </button>
        <Link
          to="/coupons"
          className="inline-flex items-center justify-center rounded-md border border-stroke py-2 px-4 text-center font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
        >
          Back to List
        </Link>
      </div>

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this coupon? This action cannot be undone."
          onConfirm={deleteCoupon}
          onCancel={() => setShowDeleteModal(false)}
          isLoading={isDeleting}
        />
      )}
    </>
  );
};

export default CouponDetails;